import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { IMAGES, STATS } from "@/data/site";
import Reveal from "@/components/Reveal";
import OrbitalEcosystem from "@/components/OrbitalEcosystem";

const TEAM = [
  { name: "Aarav Kulkarni", role: "Founder & CEO", note: "20 years in F&B operations & supply chain." },
  { name: "Meera Deshmukh", role: "Chief Culinary Officer", note: "Heads the central kitchen and R&D." },
  { name: "Karthik Rao", role: "Head of Retail", note: "Built the Pantry into 5,000+ touchpoints." },
  { name: "Sneha Iyer", role: "Head of Brand & Design", note: "Designs every plate the eye eats first." },
];

const MILESTONES = [
  ["2022", "Native Touch Group founded under EBG Group. First Millet Plaza opens in Pune."],
  ["2023", "Stories — The Millet Cafe launches in Baner. Retail SKU range hits 24 products."],
  ["2024", "GoMill kiosk model scales to 18 units across 3 cities. ProMill QSR debuts in Hyderabad."],
  ["2025", "10M+ meals served. 5,000+ retail touchpoints. Multi-city franchise expansion."],
];

const VALUES = [
  ["Wholesome", "Every recipe begins with a millet. Everything else is built around it."],
  ["Natural", "No maida, no preservatives, no shortcuts the label can't pronounce."],
  ["Responsible", "We pay 22% above mandi rates to a 240-farmer collective in Maharashtra & Karnataka."],
];

export default function About() {
  return (
    <div data-testid="page-about" className="bg-[var(--bg)]">
      {/* HERO */}
      <section className="pt-[110px] md:pt-[120px] pb-14 bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6">
            <div className="overline overline-green">About · An EBG Group Company</div>
            <h1 className="font-serif-display text-4xl md:text-6xl mt-4 leading-tight text-[var(--ink)] font-semibold">
              Rooted in Millets. <br /><span className="text-[var(--green)]">Driven by Purpose.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[var(--ink-2)] leading-relaxed max-w-lg">
              Native Touch Group is India's pioneer millet food platform — blending tradition,
              nutrition and design-led storytelling across dine-in, on-the-go and retail.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/franchise" data-testid="about-hero-franchise" className="btn-primary">Our Journey <ArrowRight size={14} /></Link>
              <Link to="/contact" data-testid="about-hero-contact" className="btn-outline">Our Impact</Link>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <div className="aspect-[5/4] overflow-hidden rounded-2xl">
              <img src={IMAGES.farmer} alt="" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="section bg-[var(--bg-soft)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <div className="overline overline-green">Our Belief</div>
            <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold">
              More than brands. <br /><span className="text-[var(--green)]">A shared belief.</span>
            </h2>
          </Reveal>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4">
            {VALUES.map(([t, d]) => (
              <Reveal key={t}>
                <div className="card-soft p-6 h-full">
                  <div className="w-11 h-11 rounded-full bg-[var(--green)] text-white flex items-center justify-center mb-4">
                    <CheckCircle2 size={18} />
                  </div>
                  <div className="font-serif-display text-2xl font-semibold text-[var(--green)]">{t}.</div>
                  <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ECOSYSTEM ORBIT (moved from Home) */}
      <OrbitalEcosystem />


      {/* STATS */}
      <section className="section bg-[var(--green-dark)] text-white">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <div className="overline text-[var(--gold-soft)]">Our Impact</div>
              <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight font-semibold">
                The numbers behind <span className="text-[var(--gold)]">the plate.</span>
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-12">
            {STATS.map((s) => (
              <div key={s.label} className="bg-white/5 border border-white/15 rounded-2xl p-7 text-center">
                <div className="font-serif-display text-4xl md:text-5xl text-[var(--gold)] font-semibold">{s.value}</div>
                <div className="kbd text-white/80 mt-3">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MILESTONES */}
      <section className="section bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="overline overline-green">The Journey</div>
            <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold max-w-3xl">
              Four years. Four brands. <span className="text-[var(--green)]">One movement.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 mt-12">
            {MILESTONES.map(([year, text], i) => (
              <Reveal key={year} delay={i * 0.06}>
                <div className="card-soft p-6 h-full">
                  <div className="font-serif-display text-4xl font-semibold text-[var(--green)]">{year}</div>
                  <p className="text-sm text-[var(--ink-2)] mt-4 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="section bg-[var(--bg-soft)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="overline overline-green">Leadership</div>
            <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold max-w-3xl">
              The hands behind <span className="text-[var(--green)]">the kitchen.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {TEAM.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.05}>
                <div className="card-soft p-6 h-full">
                  <div className="kbd text-[var(--gold)]">0{i + 1}</div>
                  <div className="font-serif-display text-xl mt-3 font-semibold text-[var(--ink)]">{t.name}</div>
                  <div className="text-xs font-semibold text-[var(--green)] mt-1.5">{t.role}</div>
                  <p className="text-sm text-[var(--ink-2)] mt-4 leading-relaxed">{t.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="bg-white border border-[var(--line-soft)] rounded-2xl px-8 md:px-14 py-14 md:py-20 text-center">
            <h2 className="font-serif-display text-3xl md:text-5xl text-[var(--ink)] font-semibold leading-tight">
              Join us on the <span className="text-[var(--green)]">millet movement.</span>
            </h2>
            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              <Link to="/franchise" data-testid="about-cta-franchise" className="btn-primary">Become a partner <ArrowRight size={14} /></Link>
              <Link to="/contact" data-testid="about-cta-contact" className="btn-outline">Get in touch</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
